import React from 'react';
import ContentLoader from 'react-content-loader';
import InputMask from 'react-input-mask';
import NumberFormat from 'react-number-format';
import { useHistory, withRouter } from 'react-router-dom';
import moment from 'moment';
import { Button, Grid, Icon, Page, Form, FormTextInput } from 'tabler-react';
import { PMT, PV } from '@formulajs/formulajs';

import PrivatePage from './PrivatePage';
import { UseDebounce } from '../components';
import { Backend } from '../services';

function ProjectSimulationPage(props) {
  const { lotId, projectId } = props.match.params;

  const history = useHistory();
  const [project, setProject] = React.useState(null);
  const [lot, setLot] = React.useState(null);
  const [availableInstallments, setAvailableInstallments] = React.useState(null);
  const [installments, setInstallments ] = React.useState(null);

  const [selectedNumberOfInstallments, setSelectedNumberOfInstallments] = React.useState(null);
  const [currentInstallment, setCurrentInstallment] = React.useState(null);
  const [fundingInstallmentValue, setFundingInstallmentValue] = React.useState(null);

  const [fundingEntry, setFundingEntry] = React.useState('');
  const [fundingEntryPercentage, setFundingEntryPercentage] = React.useState('');
  const [fundingEntryRecalculate, setFundingEntryRecalculate] = React.useState('');
  const [fundingEntryPercentageRecalculate, setFundingEntryPercentageRecalculate] = React.useState('');
  const debouncedFundingEntry = UseDebounce(fundingEntryRecalculate, 500);
  const debouncedFundingEntryPercentage = UseDebounce(fundingEntryPercentageRecalculate, 500);
  const [outstandingBalance, setOutstandingBalance] = React.useState(null);

  const [fundingIntermediaryDatePicker, setFundingIntermediaryDatePicker] = React.useState(false);
  const [fundingIntermediaryDate, setFundingIntermediaryDate] = React.useState(moment().format('DD/MM/YYYY'));
  const [fundingIntermediaryValue, setFundingIntermediaryValue] = React.useState('');
  const [fundingIntermediaries, setFundingIntermediaries] = React.useState([]);

  const [simulateButtonEnabled, setSimulateButtonEnabled] = React.useState(false);
  const [simulationStatus, setSimulationStatus] = React.useState(null);
  const [simulation, setSimulation] = React.useState(null);

  const [profile, setProfile] = React.useState({});
  const [clientName, setClientName] = React.useState('');
  const [clientEmail, setClientEmail] = React.useState('');
  const [clientMobilePhone, setClientMobilePhone] = React.useState('');
  const [clientDocument, setClientDocument] = React.useState('');
  const [observation, setObservation] = React.useState('');
  
  const [isProposal, setIsProposal] = React.useState(false);
  const [errors, setErrors] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      setLoading(true);

      try {
        const resProfile = await Backend.getProfile();
        setProfile(resProfile.data);

        const resProject = await Backend.getProject(projectId);
        setProject(resProject.data);

        const resLot = await Backend.getProjectLot(projectId, lotId);
        setLot(resLot.data);

        setOutstandingBalance(resLot.data.finalPrice);
        setAvailableInstallments(resLot.data.appData.availableInstallments);
        setInstallments(resLot.data.appData.installments);
        setCurrentInstallment(resLot.data.appData.installments[resLot.data.appData.availableInstallments[0]]);
        setSelectedNumberOfInstallments(resLot.data.appData.availableInstallments[0]);

        setLoading(false);
      } catch (err) {
        console.error(err);
        alert('Something went wrong');
      }

      setLoading(false);
    })();
  }, []);

  React.useEffect(() => {
    const dividend = parseFloat(fundingEntryRecalculate.replace(/\D/g, ''));
    if (fundingEntryRecalculate.length > 0 && !isNaN(dividend)) {
      const percentage = Math.floor(dividend/lot.finalPrice * 100)
      setFundingEntryPercentage(`${percentage}`);
      setOutstandingBalance(lot.finalPrice - dividend);
    } else {
      setFundingEntryPercentage(``);
    }
  }, [debouncedFundingEntry]);

  React.useEffect(() => {
    const percentage = parseInt(fundingEntryPercentageRecalculate.replace(/^0|\D/g, ''));
    if (!isNaN(percentage)) {
      const value = lot.finalPrice * percentage / 100;
      setFundingEntry(`${parseInt(value / 100)}`);
      setOutstandingBalance(lot.finalPrice - value);
    } else {
      setFundingEntry(``);
    }
  }, [debouncedFundingEntryPercentage]);

  React.useEffect(() => {
    if (!outstandingBalance || !currentInstallment) return;

    setSimulateButtonEnabled(currentInstallment && fundingEntryPercentage && fundingEntry);
    setSimulationStatus(null);

    if (fundingEntryPercentage) {
      const fundingEntryPercentageFloat = parseFloat(fundingEntryPercentage.replace('%', '').replace(',', '.'))/100;
      if ( fundingEntryPercentageFloat >= currentInstallment.minimuEntryPercentage) {
        setSimulationStatus('OK');
      } else {
        setSimulationStatus('WARNING_FUNDING_ENTRY');
      }
    }
  }, [outstandingBalance, currentInstallment]);

  React.useEffect(() => {
    if (!selectedNumberOfInstallments) return;

    const nearAvailableInstallment = availableInstallments.find(p => selectedNumberOfInstallments <= p) || availableInstallments[availableInstallments.length - 1];
    setCurrentInstallment(installments[nearAvailableInstallment]);
  }, [selectedNumberOfInstallments]);

  React.useEffect(() => {
    if (fundingInstallmentValue) calculateFundingInstallmentValue();
  }, [fundingIntermediaries]);

  React.useEffect(() => {
    setSimulation(null);

    if (fundingInstallmentValue !== null) {
      (async () => {
        try {
          const res = await Backend.createSimulation({
            projectId: project.id,
            identifier: lot.identifier,
            area: lot.area,
            areaPrice: lot.areaPrice,
            finalPrice: lot.finalPrice,
            kind: lot.kind,
            fundingEntry: parseInt(fundingEntry.replace(/\D/g, '')),
            fundingEntryPercent: parseInt(fundingEntryPercentage),
            outstandingBalance: parseInt(outstandingBalance),
            numberOfInstallments: parseInt(selectedNumberOfInstallments),
            rate: currentInstallment.rate,
            indexingRate: currentInstallment.indexingRate,
            fundingInstallmentValue: fundingInstallmentValue,
            fundingIntermediariesData: fundingIntermediaries
          });

          const data = res.data;
          data.simulationId = data.id;
          delete data.id;

          setSimulation(data);
        } catch (err) {
          console.error(err);
          alert('Something went wrong');
        }
      })();
    }
  }, [fundingInstallmentValue]);

  const calculateFundingInstallmentValue = () => {
    const balance = fundingIntermediaries.reduce((balance, intermediary) => {
      const monthDiff = Math.ceil(moment(intermediary.date, 'DD/MM/YYYY').diff(moment(), 'months', true));
      return balance + PV(currentInstallment.rate, monthDiff, 0, intermediary.value/100);
    }, outstandingBalance/100); 

    setFundingInstallmentValue(parseInt(-PMT(currentInstallment.rate, selectedNumberOfInstallments, balance) * 100));
  }

  const onSimulatePress = calculateFundingInstallmentValue;

  const onResimulatePress = () => {
    setFundingInstallmentValue(null);
    setFundingIntermediaries([]);
  }

  const onIntermediaryRemovePress = (index) => {
    const intermediaries = [...fundingIntermediaries];
    const intermediary = intermediaries.splice(index, 1)[0];
    setFundingIntermediaries(intermediaries);
  }

  const onSubmitPress = async () => {
    setLoading(true);

    try {
      await Backend.createProposal({
        ...simulation,
        clientName,
        clientEmail,
        clientMobilePhone,
        clientDocument,
      });

      alert('Parabéns o seu chamado foi enviado com sucesso, agora só aguardar o nosso contato')
      
      history.replace(`/projects/${projectId}`);
    } catch (err) {
      setSubmitting(false);
      setLoading(false);

      if (err.response && err.response.status === 400) {
        const fields = err.response.data.reduce((obj, i) => {
          obj[i.field] = i.message;
          return obj;
        }, {});

        alert('Parece que tem algum campo inválido, verifique o formulário');

        return setErrors(fields);
      }

      console.error(err);
      alert('Something went wrong');
    }

    setLoading(false);
  };

  const renderLot = (item) => (
    <div className="card">
      <div className="d-flex justify-content-between m-3" style={{borderBottom: '1px solid #DDD'}}>
        <div>
          <div className="primary-color" style={{fontSize: '18px', fontWeight: 800}}>{item.identifier}</div>
          <small className="d-block text-muted">
          { item.kind ?  item.kind : (
            project.kind === 'residential' ? 'Residencial' : (
              project.kind === 'business' ? 'Empresarial' : (
                project.kind === 'area' ? 'Área': (
                  project.kind === 'mixed' ? 'Misto': ''
                )
              )   
            )
          )}
          </small>
        </div>
        <div className="text-muted text-right d-flex flex-column">
          <span className={`${item.status === 'À VENDA' ? 'green-color' : (item.status === 'VENDIDO' ? 'red-color' : (item.status === 'RESERVADO' ? 'yellow-color' : ''))}`}>{item.status}</span>
        </div>
      </div>
      <div className="px-3 pb-3" style={{fontSize: '14px'}}>
        <div className="d-flex justify-content-between py-1">
          <span>Área Real (m²)</span>
          <NumberFormat
            value={item.area/100}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            displayType="text"
            fixedDecimalScale={true}
            renderText={value => <span style={{textAlign: 'right'}}>{value}</span>}/>
        </div>

        <div className="d-flex justify-content-between py-1">
          <span>Preço Final do Lote (R$/m²)</span>
          <NumberFormat
            value={item.areaPrice/100}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            displayType="text"
            fixedDecimalScale={true}
            renderText={value => <span style={{textAlign: 'right'}}>{value}</span>}/>
        </div>
          
         
        <div className="d-flex justify-content-between py-1">
          <span>Valor Final do Lote</span>
          <NumberFormat
            value={item.finalPrice/100}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            displayType="text"
            fixedDecimalScale={true}
            prefix="R$ "
            renderText={value => <span style={{textAlign: 'right', fontWeight: 700}}>{value}</span>}/>
        </div>
      </div>
    </div>
  );

  const renderSimulationNotice = () => (
    <>
    {
      fundingIntermediaries.length > 0 ?
        <div className="card">
          <div className="d-flex justify-content-between bg-red-color p-2">
            <Icon name="alert-circle" style={{marginLeft:4,marginTop:2}} />
            <p className="px-2" style={{fontSize: '12px'}}>Essas condições estão sujeitas a análise, caso queira continuar mesmo assim abra um chamado.</p>
          </div>
        </div>
      : simulationStatus === 'OK' && fundingInstallmentValue ? 
        <div className="card ">
          <div className="d-flex justify-content-between bg-green-color p-2">
            <Icon name="check-circle" style={{marginLeft:4,marginTop:2}} />
            <p className="px-2" style={{fontSize: '12px'}}>
              PARABÉNS, sua solicitação está aprovada, para continuar abra um chamado.
            </p>
          </div>
        </div>
      : simulationStatus === 'WARNING_FUNDING_ENTRY' ?
        <div className="card rounded">
          <div className="d-flex justify-content-between p-2 bg-yellow-color">
            <Icon name="alert-circle" className="yellow-color" style={{marginLeft:4,marginTop:4, fontSize: '16px'}} />
            <div className="px-2" style={{fontSize: '12px'}}>
              {
                fundingInstallmentValue ?
                <p>Essas condições estão sujeitas a análise, caso queira continuar mesmo assim abra um chamado.</p>
                :
                <>
                  <p>Essas condições não são as indicadas, confira nossa recomendação:</p>
                  <p>{selectedNumberOfInstallments} vezes = Entrada mínima de {currentInstallment.minimuEntryPercentage * 100}%</p>
                </>
              }
            </div>
          </div>
        </div>
      : null
    }
    </>
  );

  const renderSimulator = () => (
    <div className="card">
      <div className="d-flex flex-column m-3" style={{borderBottom: '1px solid #DDD'}}>
        <Form.Group label="Entrada">
          <Form.InputGroup>
            <Form.InputGroupPrepend>
              <Form.InputGroupText>
                R$
              </Form.InputGroupText>
            </Form.InputGroupPrepend>
            <Form.Input 
              type="number"
              pattern="\d*"
              value={fundingEntry} onChange={(e) => {
                let rawText = e.target.value.replace(/\D/g, '')
                if (parseInt(rawText) * 100 > lot.finalPrice) {
                  rawText = lot.finalPrice / 100
                }

                setFundingEntry(`${rawText}`);
                setFundingEntryRecalculate(`${rawText * 100}`);
              }}
              name="fundingEntry"
              error={errors && errors.fundingEntry}/>
            <Form.InputGroupAppend>
              <Form.InputGroupText>
                ,00
              </Form.InputGroupText>
            </Form.InputGroupAppend>
          </Form.InputGroup>
        </Form.Group>
        <Form.Group label="Percentual">
          <Form.InputGroup>
            <Form.Input 
              type="number"
              pattern="\d*"
              value={fundingEntryPercentage} onChange={(e) => {
                let rawText = e.target.value.replace(/^0|\D/g, '');
                while(parseInt(rawText) > 100 || rawText.charAt(0) === '0') rawText = rawText.substring(1);

                setFundingEntryPercentage(`${rawText}`);
                setFundingEntryPercentageRecalculate(`${rawText}`);
              }
            }
              name="fundingEntry"
              error={errors && errors.fundingEntry}/>
            <Form.InputGroupAppend>
              <Form.InputGroupText>
                %
              </Form.InputGroupText>
            </Form.InputGroupAppend>
          </Form.InputGroup>
        </Form.Group>
        
        <div className="d-flex justify-content-between pt-1 pb-3">
          <span>Saldo devedor</span>
          <NumberFormat
            value={outstandingBalance/100}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            displayType="text"
            fixedDecimalScale={true}
            prefix="R$ "
            renderText={value => <span style={{fontWeight: 600}}>{value}</span>}/>
        </div>

      </div>
      <div className="px-3 pb-3" style={{fontSize: '14px'}}>
        <Form.Group label="Parcelas">
          <Form.Select name="selectedNumberOfInstallments" value={selectedNumberOfInstallments} onChange={(e) => {setSelectedNumberOfInstallments(parseInt(e.target.value))}}>
            <option value="0">À vista</option>
            {
              new Array(availableInstallments[availableInstallments.length - 1]).fill(true).map((_, index) => (
                <option key={index+1} value={index + 1}>{index + 1}</option>
              ))
            }
          </Form.Select>
        </Form.Group>

        <div className="d-flex justify-content-between py-1">
          <span>Entrada Mínima</span>
          <span className="text-right" style={{fontWeight: 600}}>{currentInstallment.minimuEntryPercentage * 100}%</span>
        </div>
          
         
        <div className="d-flex justify-content-between py-1">
          <span>Juros</span>
          <span className="text-right" style={{fontWeight: 600}}>{(currentInstallment.rate * 100).toString().replace('.', ',')}%</span>
        </div>

        { 
          !currentInstallment.indexingRate ? null :
          <div className="d-flex justify-content-between py-1">
            <span>Indexação</span>
            <span className="text-right" style={{fontWeight: 600}}>{currentInstallment.indexingRate}</span>
          </div>
        }
      </div>
    </div>
  )

  const renderSimulatorResult = () => (
    <div className="card">
      <div className="d-flex flex-column m-3">
        <h5>Resultado da Simulação</h5>
        <div className="d-flex justify-content-between py-1">
          <span>Entrada ({fundingEntryPercentage}%)</span>
          <NumberFormat
            value={fundingEntry}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            displayType="text"
            fixedDecimalScale={true}
            prefix="R$ "
            renderText={value => <span>{value}</span>}/>
        </div>
        
        <div className="d-flex justify-content-between py-1">
          <span>Saldo devedor</span>
          <NumberFormat
            value={outstandingBalance/100}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            displayType="text"
            fixedDecimalScale={true}
            prefix="R$ "
            renderText={value => <span>{value}</span>}/>
        </div>

        <div className="d-flex justify-content-between py-1">
          <span>Juros {currentInstallment.indexingRate ? `(${currentInstallment.indexingRate})` : ''}</span>
          <span className="text-right">{(currentInstallment.rate * 100).toString().replace('.', ',')}%</span>
        </div>

        <div className="d-flex justify-content-between py-1">
          <span>Número de parcelas</span>
          <span className="text-right">{selectedNumberOfInstallments}</span>
        </div>

        <div className="d-flex justify-content-between py-1">
          <span>Valor da parcela</span>
          <NumberFormat
            value={fundingInstallmentValue / 100}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={2}
            displayType="text"
            fixedDecimalScale={true}
            prefix="R$ "
            renderText={value => (
              <span className={fundingIntermediaries.length > 0 ? 'red-color' : (simulationStatus === 'OK' ? 'green-color' : 'yellow-color')} style={{textAlign: 'right', fontWeight: 600}}>{value}</span>
            )}/>
        </div>
      </div>
      {
        fundingIntermediaries.length === 0 ? null :
        <div className="d-flex flex-column m-3" style={{borderTop: '1px solid #DDD'}}>
          {
            fundingIntermediaries.map((intermediary, index) => (
              <div className="d-flex justify-content-between py-1" key={index}>
                <div className="d-flex">
                  { isProposal ? null : <Icon className="red-color p-1" name="trash" link onClick={onIntermediaryRemovePress}/> }
                  <span>Intermediária ({intermediary.date})</span>
                </div>
                <NumberFormat
                  value={intermediary.value/100}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  displayType="text"
                  fixedDecimalScale={true}
                  prefix="R$ "
                  renderText={value => (<span style={{textAlign: 'right'}}>{value}</span>)}/>
              </div>
            ))
          }
        </div>
      }
    </div>
  )

  const renderIntermediaryForm = () => (
    <div className="card">
      <div className="d-flex flex-column m-3">
        <h5>Intermediárias</h5>
        <Form.Group label="Data">
          <Form.DatePicker
            defaultDate={moment().toDate()}
            format="dd/mm/yyyy"
            maxYear={moment().add(selectedNumberOfInstallments, 'months').toDate().getFullYear()}
            minYear={moment().toDate().getFullYear()}
            onChange={(date) => setFundingIntermediaryDate(moment(date).format('DD/MM/YYYY'))}
            monthLabels={[
              'Janeiro',
              'Fevereiro',
              'Março',
              'Abril',
              'Maio',
              'Junho',
              'Julho',
              'Agosto',
              'Setembro',
              'Outubro',
              'Novembro',
              'Dezembro'
            ]}
          />
        </Form.Group>
        <Form.Group label="Valor">
          <Form.InputGroup>
            <Form.InputGroupPrepend>
              <Form.InputGroupText>
                R$
              </Form.InputGroupText>
            </Form.InputGroupPrepend>
            <Form.Input 
              type="number"
              pattern="\d*"
              value={fundingIntermediaryValue} onChange={(e) => {
                let rawText = e.target.value.replace(/\D/g, '')
                if (parseInt(rawText) * 100 > lot.finalPrice) {
                  rawText = lot.finalPrice / 100
                }

                setFundingIntermediaryValue(`${rawText}`);
              }}
              name="fundingEntry"
              error={errors && errors.fundingEntry}/>
            <Form.InputGroupAppend>
              <Form.InputGroupText>
                ,00
              </Form.InputGroupText>
            </Form.InputGroupAppend>
          </Form.InputGroup>
        </Form.Group>
      </div>
      <div className="d-flex flex-column p-3" style={{borderTop: '1px solid #DDD'}}>
        <Button
          color="secondary"
          disabled={!(fundingIntermediaryDate && fundingIntermediaryValue)}
          block
          onClick={() => {
            setFundingIntermediaries([...fundingIntermediaries, {date: fundingIntermediaryDate, value: parseInt(fundingIntermediaryValue.replace(/\D/g, '') * 100)}]);
            setFundingIntermediaryValue('');
          }}
        >
          Adicionar Intermediária
        </Button>
      </div>
    </div>
  );

  const renderProposalForm = () => (
    <div className="card">
      <div className="d-flex flex-column m-3">
        <h5>Abertura de Chamado</h5>

        <Grid.Row>
          <Grid.Col>
            <h6>Dados do Corretor</h6>

            <div className="d-flex flex-column justify-content-between py-1">
              <span style={{fontWeight: 600}}>Nome</span>
              <span>{profile?.name}</span>
            </div>

            <div className="d-flex flex-column justify-content-between py-1">
              <span style={{fontWeight: 600}}>Email</span>
              <span>{profile?.email}</span>
            </div>

            <div className="d-flex flex-column justify-content-between py-1">
              <span style={{fontWeight: 600}}>Celular</span>
              <span>{profile?.mobilePhone}</span>
            </div>

            <div className="d-flex flex-column justify-content-between py-1">
              <span style={{fontWeight: 600}}>CRECI</span>
              <span>{profile?.creci}</span>
            </div>
            <div className="d-flex flex-column justify-content-between py-1">
              <span style={{fontWeight: 600}}>Imobiliária</span>
              <span>{profile?.realState}</span>
            </div>
            <div className="d-flex flex-column justify-content-between py-1">
              <span style={{fontWeight: 600}}>CNPJ Imobiliária</span>
              <span>{profile?.realStateDocument}&nbsp;</span>
            </div>
            <div className="d-flex flex-column justify-content-between py-1">
              <span style={{fontWeight: 600}}>CRECI Imobiliária</span>
              <span>{profile?.realStateCreci}&nbsp;</span>
            </div>
            <div className="d-flex flex-column justify-content-between py-1">
              <span style={{fontWeight: 600}}>Responsável Imobiliária</span>
              <span>{profile?.realStateResponsibleName}&nbsp;</span>
            </div>
            <div className="d-flex flex-column justify-content-between py-1">
              <span style={{fontWeight: 600}}>Celular Responsável</span>
              <span>{profile?.realStateResponsibleMobilePhone}&nbsp;</span>
            </div>
            <div className="d-flex flex-column justify-content-between py-1">
              <span style={{fontWeight: 600}}>Email Responsável</span>
              <span>{profile?.realStateResponsibleEmail}&nbsp;</span>
            </div>
          </Grid.Col>
        
        
        <Grid.Col>
          <h6>Dados do Cliente</h6>

          <FormTextInput
            label="Nome do responsável pela Imobiliária"
            placeholder="Nome com sobrenome"
            onChange={(e) => {setClientName(e.target.value)}}
            value={clientName}
            error={errors && errors.clientName}
          />
          <FormTextInput
            label="Email do Cliente"
            placeholder="seuemail@gmail.com"
            onChange={(e) => {setClientEmail(e.target.value)}}
            value={clientEmail}
            error={errors && errors.clientEmail}
          />
          <InputMask mask="(99) 99999-9999" value={clientMobilePhone} onChange={(e) => {setClientMobilePhone(e.target.value)}}>
            {(inputProps) => 
              <FormTextInput
                {...inputProps}
                label="Celular do Cliente"
                error={errors && errors.clientMobilePhone}
              />
            }
          </InputMask>
          
          <FormTextInput
            label="Observação"
            placeholder="Algo que você queria comentar nesse chamado ?"
            onChange={(e) => {setObservation(e.target.value)}}
            value={observation}
            error={errors && errors.observation}
          />
        </Grid.Col>
        </Grid.Row>
      </div>
    </div>
  )

  return (
    <PrivatePage>
      {
        loading ? <Page.Content><ContentLoader foregroundColor="#DDD"/></Page.Content>
        :
        <Page.Content>
          <Page.Header title="Simulador" subTitle="Preencha o formulário abaixo para fazer a simulação do lote"/>
          <Grid>
            <Grid.Row>
              <Grid.Col xs="12" sm="12" lg="4">
                <div className="card">
                  <div style={{
                    width: '100%',
                    height: '152px',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center center',
                    backgroundRepeat: 'no-repeat',
                    backgroundColor: '#EEF0F2',
                    backgroundImage: `url('${Backend.generateProjectImageUrl(project.id, project.images.find(i => i.kind === 'banner')?.id)}')`}}>
                  </div>
                </div>
                {renderLot(lot)}
                {
                  isProposal ? 
                  <>
                    {renderSimulatorResult()}
                    <Button
                      loading={submitting}
                      type="button"
                      color="secondary"
                      block
                      onClick={() => setIsProposal(false)}
                    >
                      Voltar para a simulação
                    </Button>
                  </>
                  : null
                }
              </Grid.Col>

              {
                isProposal ? null :
                <Grid.Col xs="12" sm="12" lg="4">
                  {fundingInstallmentValue === null ? renderSimulator() : 
                    <>
                      {renderSimulatorResult()}
                      {renderIntermediaryForm()}
                    </>
                  }
                </Grid.Col>
              }
              
              {
                isProposal ?
                <Grid.Col xs="12" sm="12" lg="8">
                  { renderProposalForm() }
                  <Button
                    loading={submitting}
                    type="button"
                    color="primary"
                    block
                    onClick={onSubmitPress}
                  >
                    Enviar Chamado
                  </Button>
                </Grid.Col>
                :

                <Grid.Col xs="12" sm="12" lg="4">
                { renderSimulationNotice() }
                { 
                  fundingInstallmentValue === null ?
                  <Button
                    disabled={!simulateButtonEnabled}
                    loading={submitting}
                    type="button"
                    color="primary"
                    block
                    onClick={onSimulatePress}
                  >
                    Simular
                  </Button>
                  :
                  <>
                    <Button
                      loading={submitting}
                      type="button"
                      color="primary"
                      block
                      onClick={() => setIsProposal(true)}
                    >
                      Abrir Chamado
                    </Button>
                    <Button
                      loading={submitting}
                      type="button"
                      color="secondary"
                      block
                      onClick={onResimulatePress}
                    >
                      Refazer Simulação
                    </Button>
                  </>
                }
              </Grid.Col>
              }
              
            </Grid.Row>
          </Grid>
        </Page.Content>
      }
    </PrivatePage>
  )
}

export default withRouter(ProjectSimulationPage);